import { ERROR_URL } from "./config";
import { getSession, getVisitorId } from "./identity";

interface ReportedError {
  error_type: string;
  message: string;
  stack?: string;
  page_url: string;
  metadata?: Record<string, unknown>;
}

const errorQueue: ReportedError[] = [];
const recentErrors = new Set<string>();

// The browser masks cross-origin (third-party, no-CORS) script exceptions as a
// content-free sentinel: { message: "Script error.", filename: "", lineno: 0,
// error: null } — no stack, no source, nothing to debug. They come from
// third-party scripts we can't fix (Metrika, Jivo, Clarity). Anchored so a real
// error that merely contains the phrase still reports.
const CROSS_ORIGIN_SCRIPT_ERROR = /^Script error\.?$/;

function isOwnSource(filename?: string): boolean {
  // An empty/unknown filename is the signature of the cross-origin sentinel
  // above, so treat unknown source as NOT ours. (This used to `return true`,
  // which forwarded every masked third-party "Script error." to /errors → the
  // infra-alert as service=frontend — exactly the noise this guard exists to
  // drop.)
  if (!filename) return false;
  return filename.includes(location.hostname) || filename.startsWith("/");
}

// Injected/eval'd third-party code (browser extensions, in-app mobile WebViews)
// throws with a stack whose frames are all `<anonymous>` and reference nothing
// on our origin — e.g. the Android-WebView "XMLHttpRequest is not a constructor"
// noise, whose window.onerror `filename` the browser mis-attributes to our
// same-origin page-entry bundle (so isOwnSource passes and it pages the team as
// service=frontend). Our own code can NEVER produce such a stack: the app-page
// CSP forbids eval/new Function, so every first-party frame carries its
// same-origin URL. A stack that exists but touches nothing on our origin is
// therefore not ours.
function isForeignInjectedStack(stack?: string): boolean {
  return !!stack && !stack.includes(location.hostname);
}

// Vike's client router (vike-react Client Routing) asserts that every
// window.history entry carries its own { vike } marker (vike history.js →
// assertIsEnhanced → assert(false, { state })). On ad-traffic pages that BOTH
// use hash-anchor CTAs (#pricing/#faq → popstate) AND run a deferred analytics
// script that monkey-patches history.replaceState AFTER Vike (Yandex Metrika
// strips yclid from the URL with a null state), Vike is no longer the last
// patcher in the chain, so its next history read finds state===null and throws
// `[vike][Bug] … {"state":null}`. Vike self-heals (microtask re-patch #2504 +
// popstate hard-reload fallback #2801) and the browser handles the #hash scroll
// natively, so the user flow completes — this is an upstream-owned false
// positive on ad traffic, not an actionable app bug (same class as
// sveltejs/kit#11671, a GA-monkey-patch false positive). The stack is our
// same-origin vike bundle, so isOwnSource/isForeignInjectedStack both pass it —
// hence the explicit drop. Scoped tightly to the { state } assertion (the vike
// bug tag + the `"state":` debug marker) so any OTHER Vike bug still pages.
const VIKE_BUG_TAG = "You stumbled upon a Vike bug";
function isVikeHistoryStateBug(message?: string): boolean {
  return !!message && message.includes(VIKE_BUG_TAG) && message.includes('"state":');
}

// Whether a window `error` event is worth reporting to /errors: skip the
// cross-origin sentinel, the Vike history-state false positive, injected/
// extension stacks, and anything sourced outside our own origin. Exported for
// the regression test (tests/unit/tracking-error-filter.test.ts).
export function shouldReportErrorEvent(
  message: string,
  filename?: string,
  stack?: string,
): boolean {
  if (CROSS_ORIGIN_SCRIPT_ERROR.test(message)) return false;
  if (isVikeHistoryStateBug(message)) return false;
  if (isForeignInjectedStack(stack)) return false;
  return isOwnSource(filename);
}

function queueError(err: ReportedError): void {
  const key = `${err.error_type}:${err.message}:${err.metadata?.line || ""}`;
  if (recentErrors.has(key)) return;
  recentErrors.add(key);
  if (recentErrors.size > 50) recentErrors.clear();
  errorQueue.push(err);
  flushErrors();
}

async function flushErrors(): Promise<void> {
  if (errorQueue.length === 0) return;
  const errors = errorQueue.splice(0);
  const session = getSession();
  const body = {
    visitor_id: getVisitorId(),
    session_id: session.id,
    errors,
  };
  try {
    await fetch(ERROR_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    try {
      navigator.sendBeacon(
        ERROR_URL,
        new Blob([JSON.stringify(body)], { type: "application/json" }),
      );
    } catch {}
  }
}

export function setupErrorHandlers(): void {
  window.addEventListener("error", (e) => {
    if (!shouldReportErrorEvent(e.message, e.filename, e.error?.stack)) return;
    queueError({
      error_type: "js_error",
      message: e.message,
      stack: e.error?.stack?.slice(0, 2000),
      page_url: location.href,
      metadata: { source: e.filename, line: e.lineno, col: e.colno },
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    // Vike's async popstate handler rejects with the history-state assertion
    // (see isVikeHistoryStateBug) — drop it here too, not just on window.error.
    if (isVikeHistoryStateBug(reason?.message)) return;
    if (isForeignInjectedStack(reason?.stack)) return;
    queueError({
      error_type: "unhandled_rejection",
      message: reason?.message || String(reason).slice(0, 1000),
      stack: reason?.stack?.slice(0, 2000),
      page_url: location.href,
    });
  });
}

export function trackCheckoutError(tier: string, error: string): void {
  queueError({
    error_type: "checkout_error",
    message: error,
    page_url: location.href,
    metadata: { tier },
  });
}
